import maplibregl from "maplibre-gl";
import { h, ic, toast, clear } from "../dom.ts";
import { show, goHome } from "../main.ts";
import { getMap, spinGlobe, addPin, clearPins, showRings, clearOverlay, fitBBox, fitPoints } from "../map.ts";
import { catalog, describe, aboutLine, type Catalog } from "../catalog.ts";
import { prefs, savePrefs, fmtDist, fmtRing } from "../prefs.ts";
import { SoloGame, HINT_COST, seeded, todayKey, type GameController, type RoundView } from "../game.ts";
import { resultsScreen } from "./results.ts";
import { ringRadii, pickRounds, type GameConfig, type BBox, MAX_POINTS } from "../../../shared/game.ts";

export const DAILY_CONFIG: GameConfig = { scope: { kind: "world" }, kinds: ["cap"], difficulty: "medium", rounds: 5, timer: 20, mode: "standard" };

export async function startSolo(cfg: GameConfig, fixed?: import("../../../shared/game.ts").Place[]) {
  const cat = await catalog();
  const game = new SoloGame(cfg, cat, prefs.name || "You", prefs.color, fixed);
  if (!game.count) { toast("No places match those settings"); return; }
  playScreen(game, cfg, cat, { onEnd: () => show(resultsScreen(game, cfg, cat)) });
}

export function dailyResult(): number | null {
  const v = prefs.best[`daily-${todayKey()}`];
  return typeof v === "number" ? v : null;
}

export async function startDaily() {
  const cat = await catalog();
  const rounds = pickRounds(cat.places, DAILY_CONFIG, cat.continentOf, seeded(`geopin-daily-${todayKey()}`));
  const game = new SoloGame(DAILY_CONFIG, cat, prefs.name || "You", prefs.color, rounds);
  playScreen(game, DAILY_CONFIG, cat, {
    title: "Daily challenge",
    onEnd: () => {
      const total = game.results().reduce((s, r) => s + r.points + r.bonus, 0);
      const key = `daily-${todayKey()}`;
      // only a finished daily counts; ending early lets you try again today
      if (prefs.best[key] == null && game.results().length === game.count) { prefs.best[key] = total; savePrefs(prefs); }
      show(resultsScreen(game, DAILY_CONFIG, cat, { daily: true }));
    },
  });
}

interface PlayOpts { onEnd: () => void; title?: string; onQuit?: () => void }

/** Shared by solo and multiplayer: renders whatever the controller's view says. */
export function playScreen(game: GameController, cfg: GameConfig, cat: Catalog, opts: PlayOpts) {
  spinGlobe(false);
  const map = getMap();
  const root = h("div", { class: "hud passthrough" });
  show(root);

  let pending: maplibregl.Marker | null = null;
  let usedHint = false;
  let renderedKey = "";
  let raf = 0;
  let ended = false;

  const onClick = (e: maplibregl.MapMouseEvent) => {
    const v = game.view();
    if (v.phase !== "playing" || v.players.find(p => p.id === v.me)?.hasGuessed) return;
    const ll = e.lngLat;
    if (pending) pending.setLngLat(ll);
    else pending = addPin(ll, "guess");
    renderControls();
  };
  map.on("click", onClick);
  const onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement)?.tagName === "INPUT") return;
    if (e.key === "Enter" && !e.repeat) {
      // handled here; stop the same keypress from also "clicking" the button that gets focus next
      e.preventDefault();
      const v = game.view();
      if (v.phase === "playing") lockIn();
      else if (v.phase === "reveal" && !game.multiplayer) game.next();
    }
  };
  addEventListener("keydown", onKey);

  const cleanup = () => {
    ended = true;
    map.off("click", onClick);
    removeEventListener("keydown", onKey);
    cancelAnimationFrame(raf);
    clearPins(); clearOverlay();
    pending = null;
  };

  function lockIn() {
    const v = game.view();
    if (v.phase !== "playing") return;
    if (!pending) { toast(innerWidth < 820 ? "Tap the map to drop your pin" : "Click the map to drop your pin"); return; }
    const ll = pending.getLngLat();
    game.guess(ll.lat, ll.lng, usedHint);
    if (game.multiplayer) renderControls();
  }

  function hintBox(v: RoundView): BBox | null {
    if (cfg.scope.kind !== "country") return cat.byCountry.get(v.place.c)?.bbox ?? null;
    if (v.place.r) return cat.byCountry.get(v.place.c)?.regions.find(r => r.code === v.place.r)?.bbox ?? null;
    return null;
  }

  // -------------------------------------------------- per-frame timer
  const tick = () => {
    const v = game.view();
    const ringEl = root.querySelector<SVGCircleElement>(".prompt .ring circle:last-of-type");
    const label = root.querySelector<HTMLElement>(".prompt .ring span");
    if (v.phase === "playing" && v.endsAt && ringEl && label) {
      const left = Math.max(0, v.endsAt - Date.now());
      const frac = left / (v.timerSec * 1000);
      ringEl.setAttribute("stroke-dashoffset", String(157 * (1 - frac)));
      label.textContent = String(Math.ceil(left / 1000));
      ringEl.closest(".ring")?.classList.toggle("warn", left < 5000);
    }
    const nx = root.querySelector<HTMLElement>("[data-next-in]");
    if (nx && v.nextAt) nx.textContent = `Next round in ${Math.max(0, Math.ceil((v.nextAt - Date.now()) / 1000))}s`;
    if (!ended) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  // -------------------------------------------------- render
  function render() {
    if (ended) return;
    const v = game.view();
    if (v.phase === "ended") { cleanup(); opts.onEnd(); return; }
    const key = `${v.round}:${v.phase}`;
    if (key !== renderedKey) {
      renderedKey = key;
      clearPins(); clearOverlay();
      pending = null;
      if (v.phase === "playing") { usedHint = false; fitBBox(v.bbox, { maxZoom: 7 }); }
      if (v.phase === "reveal") drawReveal(v);
    }
    clear(root);
    root.append(...(v.phase === "playing" ? playingHud(v) : revealHud(v)));
    root.querySelector<HTMLElement>("[autofocus]")?.focus({ preventScroll: true });
  }

  function drawReveal(v: RoundView) {
    const ans: [number, number] = [v.place.lng!, v.place.lat!];
    const rings = ringRadii(v.scale);
    const points: [number, number][] = [ans];
    const links: [number, number][] = [];
    for (const p of v.players) {
      if (!p.guess) continue;
      const g: [number, number] = [p.guess.lng, p.guess.lat];
      const mine = p.id === v.me;
      addPin(g, mine ? "guess" : p.color, game.multiplayer ? (mine ? "You" : p.name) : undefined);
      points.push(g);
      if (mine) links.push(g);
    }
    addPin(ans, "answer", v.place.n, true);
    showRings(ans, rings.map(r => r.km), links);
    // frame the answer plus guesses, but don't zoom out to the whole world for one wild pin
    const near = points.filter(p => Math.abs(p[1] - ans[1]) < 40 && Math.abs(p[0] - ans[0]) < 60);
    const ringDeg = rings[2].km / 111;
    fitPoints([...near, [ans[0] - ringDeg, ans[1] - ringDeg * 0.6], [ans[0] + ringDeg, ans[1] + ringDeg * 0.6]], 8);
  }

  function myStreak(): number {
    let s = 0;
    for (const r of game.results()) s = r.points >= 750 ? s + 1 : 0;
    return s;
  }

  function progress(v: RoundView) {
    const res = game.results();
    return h("div", { class: "progress", "aria-hidden": "true" }, Array.from({ length: v.totalRounds }, (_, i) => {
      const r = res[i];
      const cls = i === v.round && v.phase === "playing" ? "now" : r ? (r.points >= 750 ? "good" : r.points >= 300 ? "ok" : "miss") : "";
      return h("i", { class: cls });
    }));
  }

  function topStats(v: RoundView) {
    const me = v.players.find(p => p.id === v.me);
    const streak = myStreak();
    return h("div", { class: "hud-tr" },
      h("div", { class: "stat score" }, h("small", {}, "Score"), h("b", {}, (me?.score ?? 0).toLocaleString())),
      streak >= 2 ? h("div", { class: "stat streak" }, h("small", {}, "Streak"), h("b", {}, `×${streak}`)) : null);
  }

  function playingHud(v: RoundView): HTMLElement[] {
    const me = v.players.find(p => p.id === v.me);
    const locked = !!me?.hasGuessed;
    const kindWord = v.place.t === "cap" ? "Find the capital" : v.place.t === "rcap" ? `Find the ${(cat.byCountry.get(v.place.c)?.regionWord ?? "state").toLowerCase()} capital` : v.place.t === "mark" ? "Find the landmark" : "Find the city";
    const hb = hintBox(v);
    const els: HTMLElement[] = [
      h("div", { class: "hud-tl" }, h("span", {}, opts.title ? `${opts.title} · ` : "", "Round ", h("b", {}, String(v.round + 1)), ` of ${v.totalRounds}`), progress(v)),
      h("div", { class: "prompt", role: "status", "aria-live": "polite" },
        v.endsAt ? h("div", { class: "ring", "aria-hidden": "true" },
          svgRing(), h("span", {}, String(Math.ceil((v.endsAt - Date.now()) / 1000)))) : null,
        h("div", {}, h("small", {}, kindWord), h("strong", {}, v.place.n), h("em", {}, cfg.difficulty === "expert" && v.place.t !== "cap" ? (cat.byCountry.get(v.place.c)?.name ?? "") : describe(v.place as any, cat)))),
      topStats(v),
    ];
    if (game.multiplayer) {
      const total = v.playerCount ?? v.players.length;
      const done = v.guessedCount ?? v.players.filter(p => p.hasGuessed).length;
      els.push(h("div", { class: "locked" }, h("h2", {}, `Locked in · ${done} of ${total}`),
        total > 8 ? h("div", { class: "lock-bar", "aria-hidden": "true" }, h("i", { style: `width:${Math.round((done / Math.max(1, total)) * 100)}%` })) : null,
        (total > 8 ? [] : v.players.slice(0, 8)).map(p => h("div", { class: p.hasGuessed ? "" : "waiting" },
          h("span", { class: "avatar sm", style: `background:${p.color}` }, p.name[0]?.toUpperCase() ?? "?"),
          p.id === v.me ? "You" : p.name, p.hasGuessed ? h("span", { class: "ok" }, ic("check")) : h("span", { style: "margin-left:auto" }, "…")))));
    }
    if (locked) {
      els.push(h("div", { class: "hud-bottom" }, h("div", { class: "btn dark big", style: "pointer-events:none" }, "Locked in. Waiting for others…")));
    } else {
      els.push(h("div", { class: "hud-bottom" },
        hb ? h("button", { class: "btn dark big", disabled: usedHint || undefined, "aria-label": `Hint: zoom to the area, costs ${HINT_COST} points`,
          onclick: () => { usedHint = true; fitBBox(hb, { maxZoom: 8 }); renderControls(); } },
          h("span", { style: "color:var(--gold);display:flex" }, ic("hint")), h("span", { class: "hud-hint-label" }, usedHint ? "Hint used" : "Hint"),
          h("span", { class: "hud-hint-label", style: "color:var(--coral)" }, `−${HINT_COST}`)) : null,
        h("button", { class: "btn lime big", onclick: lockIn, "data-lock": "" }, pending ? "Lock in pin" : (innerWidth < 820 ? "Tap the map" : "Click the map"))));
      if (!pending) els.push(h("div", { class: "tap-tip" }, "Drag to pan, scroll or pinch to zoom, then drop your pin"));
    }
    const mpHost = game.multiplayer && v.players.find(p => p.id === v.me)?.isHost;
    els.push(h("div", { class: "quit", style: "display:flex;gap:8px" },
      mpHost ? h("button", { class: "btn dark sm", onclick: () => { if (confirm("End the game for everyone and show final scores?")) (game as any).endEarly(); } }, "End game") : null,
      h("button", { class: "btn dark sm", onclick: quit }, game.multiplayer ? "Leave" : "End game")));
    return els;
  }

  function renderControls() {
    if (game.view().phase === "playing") { clear(root); root.append(...playingHud(game.view())); }
  }

  function revealHud(v: RoundView): HTMLElement[] {
    const me = v.players.find(p => p.id === v.me);
    const pts = (me?.points ?? 0) + (me?.bonus ?? 0);
    const dist = me?.distanceKm ?? -1;
    const rings = ringRadii(v.scale);
    const inRing = rings.find(r => dist >= 0 && dist <= r.km);
    const isLast = v.round >= v.totalRounds - 1;
    const sorted = [...v.players].sort((a, b) => (a.distanceKm ?? 1e9) < 0 ? 1 : (b.distanceKm ?? 1e9) < 0 ? -1 : (a.distanceKm ?? 0) - (b.distanceKm ?? 0));
    const els: HTMLElement[] = [
      h("div", { class: "hud-tl" }, h("span", {}, opts.title ? `${opts.title} · ` : "", "Round ", h("b", {}, String(v.round + 1)), ` of ${v.totalRounds}`), progress(v)),
      h("div", { class: "reveal", role: "status", "aria-live": "polite" },
        h("div", { class: "reveal-top" }, h("h2", {}, dist < 0 ? "Time’s up" : `${fmtDist(dist)} away`), h("span", { class: "pts" }, `+${pts.toLocaleString()}`)),
        h("div", { class: "reveal-place" }, h("b", {}, v.place.n), ` · ${describe(v.place as any, cat)}`),
        h("div", { class: "badges" },
          dist < 0 ? h("span", { class: "bad" }, "No pin dropped") :
            inRing ? h("span", { class: "good" }, inRing.points === MAX_POINTS ? "Perfect zone" : `Inside the ${fmtRing(inRing.km)} ${prefs.units} ring`) : h("span", { class: "bad" }, "Outside the rings"),
          me?.bonus ? h("span", {}, `+${me.bonus} speed`) : null,
          me?.hint ? h("span", {}, `Hint −${HINT_COST}`) : null,
          game.multiplayer && sorted[0]?.id !== v.me && (sorted[0]?.distanceKm ?? -1) >= 0 ? h("span", {}, `${sorted[0].name} was closest (${fmtDist(sorted[0].distanceKm!)})`) : null),
        h("div", { class: "about" }, ic("info"), h("div", {}, aboutLine(fullPlace(v, cat), cat))),
        game.multiplayer
          ? h("div", { class: "reveal-go" }, h("span", { class: "muted", "data-next-in": "" }, isLast ? "Final scores coming up" : "Next round soon"))
          : h("div", { class: "reveal-go" },
              h("button", { class: "btn lime big", autofocus: true, onclick: () => game.next() }, isLast ? "See results" : "Next round"),
              isLast ? null : h("button", { class: "btn", onclick: quit }, "End game"))),
      topStats(v),
      h("div", { class: "ring-legend", "aria-hidden": "true" },
        rings.map((r, i) => h("div", {}, h("i", { style: `border-color:${["#E0A800", "#5DA92A", "#2B8FD0", "#7F95B8"][i]}` }),
          `${fmtRing(r.km)} ${prefs.units}`, h("span", { class: "muted" }, ` ${r.points.toLocaleString()} pts`)))),
    ];
    if (game.multiplayer) {
      els.push(h("div", { class: "standings" }, h("h2", {}, "This round"),
        roundList(sorted, v.me).map(({ p, i }) => h("div", { class: `line${p.id === v.me ? " me" : ""}` }, h("b", {}, String(i + 1)),
          h("span", { class: "avatar sm", style: `background:${p.color}` }, p.name[0]?.toUpperCase() ?? "?"),
          p.id === v.me ? "You" : p.name, h("span", { class: "d" }, (p.distanceKm ?? -1) >= 0 ? fmtDist(p.distanceKm!) : "—")))));
    }
    return els;
  }

  function quit() {
    if (game.multiplayer) { if (!confirm("Leave this game?")) return; }
    game.quit();
    cleanup();
    if (opts.onQuit) opts.onQuit();
    else if (!game.multiplayer && game.results().length) opts.onEnd();
    else goHome();
  }

  game.onChange(render);
  render();
}

/** Top 6 closest this round, plus you at your own position if you're further down. */
function roundList(sorted: import("../game.ts").Player[], me: string) {
  const out = sorted.slice(0, 6).map((p, i) => ({ p, i }));
  const mine = sorted.findIndex(p => p.id === me);
  if (mine >= 6) out.push({ p: sorted[mine], i: mine });
  return out;
}

function fullPlace(v: RoundView, cat: Catalog) {
  return cat.places.find(p => p.id === v.place.id) ?? { ...v.place, p: 0, d: 2, lat: v.place.lat ?? 0, lng: v.place.lng ?? 0 } as any;
}

function svgRing(): SVGElement {
  const t = document.createElement("template");
  t.innerHTML = '<svg width="56" height="56" viewBox="0 0 58 58"><circle cx="29" cy="29" r="25" fill="none" stroke="#1E3A64" stroke-width="6"/><circle cx="29" cy="29" r="25" fill="none" stroke="#8BD43A" stroke-width="6" stroke-linecap="round" stroke-dasharray="157" stroke-dashoffset="0" transform="rotate(-90 29 29)"/></svg>';
  return t.content.firstElementChild as SVGElement;
}
